import { useEffect, useRef, useState } from "react";

/* ─────────────────────────────────────────
   PromptPay payload builder (EMVCo TLV + CRC16)
   รองรับ 2 รูปแบบ:
   - เบอร์มือถือ (9-10 หลัก)       → sub-tag 01, ใส่ prefix 0066
   - เลขบัตร ปชช./ผู้เสียภาษี (13 หลัก) → sub-tag 02, ใช้เลขดิบ 13 หลัก
───────────────────────────────────────── */
export function isValidPromptPayId(raw) {
  const digits = (raw || "").replace(/[^0-9]/g, "");
  return /^0\d{8,9}$/.test(digits) || digits.length === 13;
}

export function buildPromptPayPayload(id, amount = 0) {
  const digitsOnly   = id.replace(/[^0-9]/g, "");
  const isCitizenId  = digitsOnly.length === 13;
  const idTag        = isCitizenId ? "02" : "01";
  const idValue      = isCitizenId ? digitsOnly : "0066" + digitsOnly.replace(/^0/, "");

  const tlv = (tag, value) => {
    const len = value.length.toString().padStart(2, "0");
    return `${tag}${len}${value}`;
  };

  const tag00 = tlv("00", "01");
  const tag01 = tlv("01", amount > 0 ? "12" : "11");
  const ppMerchant = tlv("00", "A000000677010111") + tlv(idTag, idValue);
  const tag29 = tlv("29", ppMerchant);
  const tag53 = tlv("53", "764");
  const tag54 = amount > 0 ? tlv("54", amount.toFixed(2)) : "";
  const tag58 = tlv("58", "TH");

  const withoutCRC = tag00 + tag01 + tag29 + tag53 + tag54 + tag58 + "6304";

  let crc = 0xffff;
  for (let i = 0; i < withoutCRC.length; i++) {
    crc ^= withoutCRC.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
    }
    crc &= 0xffff;
  }
  const crcHex = crc.toString(16).toUpperCase().padStart(4, "0");
  return withoutCRC + crcHex;
}

/* ─────────────────────────────────────────
   PromptPay Config — ค่าเริ่มต้น/fallback ถ้าโหลดจาก API ไม่สำเร็จ
   ค่าจริงตั้งได้ที่ แอดมิน → แก้ไขใบเสร็จ → ตั้งค่าบัญชีรับเงิน
───────────────────────────────────────── */
export const PROMPTPAY_CONFIG = {
  phoneNumber: "0999344217",
  accountName: "EURNGKUM Guest House",
  bankName:    "ธนาคารกสิกรไทย",
};

const API = process.env.REACT_APP_API_URL || "";

/* module-level cache — โหลดจาก API แค่ครั้งเดียว ทุกหน้า/คอมโพเนนต์ share กัน */
let cachedConfig = null;
let fetchPromise = null;
const subscribers = new Set();

function fetchPromptPayConfig() {
  if (!fetchPromise) {
    fetchPromise = fetch(`${API}/api/promptpay`)
      .then(r => r.json())
      .then(d => {
        cachedConfig = {
          phoneNumber: d.phoneNumber?.trim() || PROMPTPAY_CONFIG.phoneNumber,
          accountName: d.accountName?.trim() || PROMPTPAY_CONFIG.accountName,
          bankName:    d.bankName?.trim()    || PROMPTPAY_CONFIG.bankName,
        };
        subscribers.forEach(fn => fn(cachedConfig));
      })
      .catch(() => { cachedConfig = PROMPTPAY_CONFIG; });
  }
  return fetchPromise;
}

/* เรียกหลังบันทึกค่าพร้อมเพย์ใหม่ — เคลียร์ cache แล้วดึงใหม่ ให้ QR ทุกหน้าที่เปิดค้างอยู่อัปเดตทันทีไม่ต้องรีเฟรช */
export function refreshPromptPayConfig() {
  fetchPromise = null;
  return fetchPromptPayConfig();
}

/* hook — คืนค่าพร้อมเพย์ปัจจุบัน (default fallback ก่อน แล้วอัปเดตเมื่อโหลดเสร็จ/เปลี่ยนแปลง) */
export function usePromptPayConfig() {
  const [config, setConfig] = useState(cachedConfig || PROMPTPAY_CONFIG);

  useEffect(() => {
    subscribers.add(setConfig);
    if (cachedConfig) setConfig(cachedConfig);
    else fetchPromptPayConfig();
    return () => subscribers.delete(setConfig);
  }, []);

  return config;
}

/* ─────────────────────────────────────────
   QR Canvas Component (fallback เป็น API ภายนอกถ้าโหลด lib ไม่ได้)
───────────────────────────────────────── */
export function PromptPayQR({ amount, size = 220 }) {
  const canvasRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const config = usePromptPayConfig();

  useEffect(() => {
    if (!canvasRef.current) return;
    const payload = buildPromptPayPayload(config.phoneNumber, amount);
    import("qrcode")
      .then((QRCode) => {
        QRCode.toCanvas(canvasRef.current, payload, {
          errorCorrectionLevel: "M",
          width: size,
          margin: 2,
          color: { dark: "#1a1a2e", light: "#ffffff" },
        }, () => setLoaded(true));
      })
      .catch(() => setLoaded("api"));
  }, [amount, size, config.phoneNumber]);

  if (loaded === "api") {
    const payload = buildPromptPayPayload(config.phoneNumber, amount);
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(payload)}&bgcolor=ffffff&color=1a1a2e&margin=8`;
    return (
      <img src={qrApiUrl} alt="PromptPay QR" width={size} height={size}
        style={{ borderRadius: 12, display: "block" }} />
    );
  }

  return (
    <canvas ref={canvasRef}
      style={{ borderRadius: 12, display: "block", opacity: loaded ? 1 : 0, transition: "opacity 0.3s" }} />
  );
}
